// src/pages/RealizationDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { fadeIn } from '../styles/animations';
import { realizationsService, categoriesService } from '../services/api';
import Button from '../components/ui/Button';

const RealizationDetailsWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const RealizationHero = styled.section`
  position: relative;
  height: 50vh;
  min-height: 400px;
  max-height: 600px;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7));
    z-index: 1;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.light};
  z-index: 2;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const RealizationCategory = styled.span`
  display: inline-block;
  padding: 0.3rem 1rem;
  border-radius: ${props => props.theme.borderRadius.small};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.light};
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const RealizationTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.xxxl};
  max-width: 800px;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const RealizationLocation = styled.div`
  font-size: ${props => props.theme.fontSizes.medium};
  opacity: 0.9;
`;

const RealizationContent = styled.section`
  background-color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
  animation: ${fadeIn} 1s ease-out forwards;
  
  p, ul, ol {
    margin-bottom: ${props => props.theme.spacing.md};
    line-height: 1.8;
  }
  
  h2 {
    font-size: ${props => props.theme.fontSizes.xl};
    margin: ${props => props.theme.spacing.lg} 0 ${props => props.theme.spacing.md};
  }
  
  h3 {
    font-size: ${props => props.theme.fontSizes.large};
    margin: ${props => props.theme.spacing.md} 0;
  }
`;

const DescriptionSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.lg} 0;
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ImageItem = styled.div`
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: transform ${props => props.theme.transitions.medium};
  
  &:hover {
    transform: scale(1.02);
  }
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const RelatedProjects = styled.section`
  background-color: ${props => props.theme.colors.gray};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const RelatedContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const RelatedTitle = styled.h2`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  &:after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
    margin: ${props => props.theme.spacing.sm} auto 0;
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  transition: all ${props => props.theme.transitions.medium};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    .related-image img {
      transform: scale(1.05);
    }
  }
`;

const RelatedImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
`;

const RelatedContent = styled.div`
  padding: ${props => props.theme.spacing.md};
`;

const RelatedProjectTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: ${props => props.theme.spacing.sm};
  
  a {
    color: ${props => props.theme.colors.textMain};
    text-decoration: none;
    transition: color ${props => props.theme.transitions.fast};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const RelatedLocation = styled.p`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: 600;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid #eee;
`;

const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  text-decoration: none;
  transition: color ${props => props.theme.transitions.fast};
  
  svg {
    transition: transform ${props => props.theme.transitions.fast};
  }
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
  
  &.prev:hover svg {
    transform: translateX(-5px);
  }
  
  &.next:hover svg {
    transform: translateX(5px);
  }
  
  &.prev svg {
    margin-right: 0.5rem;
  }
  
  &.next svg {
    margin-left: 0.5rem;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  h2 {
    margin-bottom: ${props => props.theme.spacing.md};
  }
  
  p {
    margin-bottom: ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.textLight};
  }
`;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl} 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textLight};
  }
`;

const InfoItem = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  
  h3 {
    font-size: ${props => props.theme.fontSizes.medium};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
  }
`;

const BlogLink = styled.div`
  background-color: ${props => props.theme.colors.gray};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.medium};
  margin: ${props => props.theme.spacing.lg} 0;
`;

const RealizationDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [realization, setRealization] = useState(null);
    const [category, setCategory] = useState(null);
    const [relatedProjects, setRelatedProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRealizationDetails = async () => {
            try {
                setLoading(true);
                setError(null);

                // Récupérer la réalisation
                const realizationData = await realizationsService.getById(id);
                if (!realizationData) {
                    throw new Error("Réalisation introuvable");
                }

                setRealization(realizationData);

                // Récupérer la catégorie si elle existe
                if (realizationData.category_id && realizationData.category_slug) {
                    try {
                        const categoryData = await categoriesService.getBySlug(realizationData.category_slug);
                        setCategory(categoryData);

                        // Récupérer les réalisations liées (même catégorie)
                        const relatedData = await realizationsService.getByCategory(realizationData.category_slug);

                        // Filtrer pour exclure la réalisation actuelle et limiter à 3
                        let related = [];
                        if (relatedData && relatedData.realizations) {
                            related = relatedData.realizations.filter(p => p.id !== realizationData.id).slice(0, 3);
                        } else if (Array.isArray(relatedData)) {
                            related = relatedData.filter(p => p.id !== realizationData.id).slice(0, 3);
                        }
                        setRelatedProjects(related);
                    } catch (categoryError) {
                        console.error("Erreur lors du chargement de la catégorie:", categoryError);
                        // Ne pas échouer complètement si seulement la catégorie pose problème
                    }
                }

            } catch (err) {
                console.error("Erreur lors du chargement de la réalisation:", err);
                setError("Nous n'avons pas pu charger cette réalisation. Veuillez réessayer ultérieurement.");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchRealizationDetails();
            window.scrollTo(0, 0);
        }
    }, [id]);

    if (loading) {
        return (
            <RealizationDetailsWrapper>
                <LoadingIndicator>
                    <p>Chargement de la réalisation...</p>
                </LoadingIndicator>
            </RealizationDetailsWrapper>
        );
    }

    if (error || !realization) {
        return (
            <RealizationDetailsWrapper>
                <ErrorMessage>
                    <h2>Réalisation introuvable</h2>
                    <p>{error || "Cette réalisation n'existe pas ou a été supprimée."}</p>
                    <Button variant="primary" to="/realisations">
                        Voir toutes les réalisations
                    </Button>
                </ErrorMessage>
            </RealizationDetailsWrapper>
        );
    }

    // Préparer l'URL de l'image principale
    const featuredImageUrl = realization.featured_image
        ? `https://decko.fr/uploads/${realization.featured_image}`
        : 'https://via.placeholder.com/1200x600?text=Decko+Realisations';

    return (
        <RealizationDetailsWrapper>
            <Helmet>
                <title>{realization.title} - Decko | Réalisations</title>
                <meta name="description" content={realization.description} />
            </Helmet>

            <RealizationHero>
                <HeroImage src={featuredImageUrl} alt={realization.title} />
                <HeroContent>
                    {category && (
                        <RealizationCategory>{category.name}</RealizationCategory>
                    )}
                    <RealizationTitle>{realization.title}</RealizationTitle>
                    {realization.location && (
                        <RealizationLocation>Localisation : {realization.location}</RealizationLocation>
                    )}
                </HeroContent>
            </RealizationHero>

            <RealizationContent>
                <ContentContainer>
                    <DescriptionSection>
                        <h2>Description du projet</h2>
                        <p>{realization.description}</p>

                        {/* Infos supplémentaires si disponibles */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', margin: '2rem 0' }}>
                            {category && (
                                <InfoItem>
                                    <h3>Type de projet</h3>
                                    <p>{category.name}</p>
                                </InfoItem>
                            )}
                            {realization.location && (
                                <InfoItem>
                                    <h3>Localisation</h3>
                                    <p>{realization.location}</p>
                                </InfoItem>
                            )}
                        </div>
                    </DescriptionSection>

                    {/* Lien vers l'article de blog associé si disponible */}
                    {realization.blog_post_slug && (
                        <BlogLink>
                            <h3>Pour en savoir plus</h3>
                            <p>Découvrez plus de détails sur ce projet dans notre article de blog dédié.</p>
                            <Button variant="secondary" to={`/blog/${realization.blog_post_slug}`}>
                                Lire l'article complet
                            </Button>
                        </BlogLink>
                    )}

                    {/* Galerie d'images */}
                    {realization.images && realization.images.length > 0 && (
                        <>
                            <h2>Galerie photos</h2>
                            <ImagesGrid>
                                {realization.images.map((image, index) => (
                                    <ImageItem key={image.id || index}>
                                        <img
                                            src={`https://decko.fr/uploads/${image.filename}`}
                                            alt={image.alt_text || `Image ${index + 1} de ${realization.title}`}
                                        />
                                    </ImageItem>
                                ))}
                            </ImagesGrid>
                        </>
                    )}

                    {/* Navigation */}
                    <Navigation>
                        <NavLink to="/realisations" className="prev">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z" />
                            </svg>
                            Retour aux réalisations
                        </NavLink>
                    </Navigation>
                </ContentContainer>
            </RealizationContent>

            {/* Réalisations similaires */}
            {relatedProjects.length > 0 && (
                <RelatedProjects>
                    <RelatedContainer>
                        <RelatedTitle>Projets similaires</RelatedTitle>
                        <RelatedGrid>
                            {relatedProjects.map(project => (
                                <RelatedCard key={project.id}>
                                    <RelatedImage className="related-image">
                                        <img
                                            src={project.featured_image
                                                ? `https://decko.fr/uploads/${project.featured_image}`
                                                : 'https://via.placeholder.com/400x200?text=Decko+Realisations'}
                                            alt={project.title}
                                        />
                                    </RelatedImage>
                                    <RelatedContent>
                                        {project.location && (
                                            <RelatedLocation>{project.location}</RelatedLocation>
                                        )}
                                        <RelatedProjectTitle>
                                            <Link to={`/realisations/${project.id}`}>{project.title}</Link>
                                        </RelatedProjectTitle>
                                        <p>{project.description}</p>
                                        <Button
                                            variant="text"
                                            to={project.blog_post_slug
                                                ? `/blog/${project.blog_post_slug}`
                                                : `/realisations/${project.id}`}
                                        >
                                            Voir le projet
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                                            </svg>
                                        </Button>
                                    </RelatedContent>
                                </RelatedCard>
                            ))}
                        </RelatedGrid>
                    </RelatedContainer>
                </RelatedProjects>
            )}
        </RealizationDetailsWrapper>
    );
};

export default RealizationDetails;